import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Smartphone, Tags, Settings, LogOut, Store, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { absUrl } from "@/lib/helpers";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard", perm: "dashboard" },
  { to: "/admin/produk", label: "Produk", icon: Package, testid: "admin-nav-produk", perm: "products" },
  { to: "/admin/perangkat", label: "Merek & Tipe HP", icon: Smartphone, testid: "admin-nav-perangkat", perm: "devices" },
  { to: "/admin/kategori", label: "Kategori", icon: Tags, testid: "admin-nav-kategori", perm: "categories" },
  { to: "/admin/pengaturan", label: "Pengaturan Toko", icon: Settings, testid: "admin-nav-pengaturan", perm: "settings" },
  { to: "/admin/users", label: "Manajemen User", icon: Users, testid: "admin-nav-users", perm: "users" },
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const { settings } = useStore();
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
      isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-secondary"
    }`;

  return (
    <div className="min-h-screen bg-background lg:flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card p-4">
        <div className="flex items-center gap-2.5 px-2 py-3">
          {settings?.logo_url && <img src={absUrl(settings.logo_url)} alt="logo" className="h-9 w-9 rounded-xl object-cover" />}
          <span className="font-heading font-extrabold">{settings?.store_name || "UPPLESORIES"}</span>
        </div>
        <nav className="mt-4 flex flex-1 flex-col gap-1">
          {LINKS.filter((l) => user?.role === "owner" || l.perm === "dashboard" || (l.perm !== "users" && (user?.permissions || []).includes(l.perm))).map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkCls} data-testid={l.testid}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => navigate("/")} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-muted-foreground hover:bg-secondary"><Store className="h-4 w-4" /> Lihat Toko</button>
        <button data-testid="admin-logout-btn" onClick={doLogout} className="mt-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-destructive hover:bg-destructive/10"><LogOut className="h-4 w-4" /> Keluar</button>
      </aside>

      {/* Mobile top nav */}
      <div className="lg:hidden sticky top-0 z-20 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <span className="font-heading font-extrabold">{settings?.store_name || "Admin"}</span>
          <button data-testid="admin-logout-btn-mobile" onClick={doLogout} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-destructive"><LogOut className="h-4 w-4" /> Keluar</button>
        </div>
        <nav className="flex gap-1 overflow-x-auto no-scrollbar px-3 pb-2">
          {LINKS.filter((l) => user?.role === "owner" || l.perm === "dashboard" || (l.perm !== "users" && (user?.permissions || []).includes(l.perm))).map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkCls}><l.icon className="h-4 w-4" /> {l.label}</NavLink>
          ))}
        </nav>
      </div>

      <main className="flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8"><Outlet /></main>
    </div>
  );
}
